import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import {
  Paper,
  TextField,
  Chip,
  Button,
  Box,
  Alert,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SortableTable, {
  SortableColumn,
} from "../../components/common/SortableTable";
import Pagination from "../../components/ui/Pagination";
import { usePagination } from "../../hooks/usePagination";
import CustomLoading from "../../components/common/CustomLoading";
import EditIcon from "../../assets/icons/EditIcon.svg";
import DeleteIcon from "../../assets/icons/DeleteIcon.svg";
import apiService, { parsePromoCodesListResponse } from "../../services/api";
import { DATE_FORMATS } from "../../constants";
import { useLocation, useNavigate } from "react-router-dom";
import SearchableSelectInput from "../../components/forms/SearchableSelectInput";
import DeleteConfirmDialog from "../../components/common/DeleteConfirmDialog";
import { useNotification } from "../../contexts/NotificationContext";
import useCodeCommonByCategory from "../../hooks/useCodeCommonByCategory";
import {
  findExpiredPromoStatusCodeValue,
  isPromoExpiredByExpiryDate,
  pickPromoExpiryRaw,
  resolvePromoStatusCodeValue,
  statusCodeValueFromApi,
} from "./promoStatusUtils";

type PromoListLocationState = {
  codeName?: string;
  status?: string;
  couponType?: string;
  couponDiscountType?: string;
  page?: number;
};

export type PromoDiscountType = 1 | 2;

export interface PromoCodeRow {
  id: string;
  codeName: string;
  description: string;
  discountType: number;
  type: number;
  discountValue: string;
  minBooking: string;
  validityDates: string;
  /** True when expiry date is today or in the past (no longer a future date). */
  isExpiredByDate: boolean;
  statusCodeValue: string;
  usedCount: number;
  usageLimit: number;
  isReferralCoupon: boolean;
  isUserAssignable: boolean;
  isCouponReuseable: boolean;
  actions?: string;
}

function parsePromoListBool(v: unknown, defaultWhenMissing: boolean): boolean {
  if (v === undefined || v === null || v === "") return defaultWhenMissing;
  if (v === true || v === 1) return true;
  if (v === false || v === 0) return false;
  const s = String(v).trim().toLowerCase();
  if (s === "true" || s === "1") return true;
  if (s === "false" || s === "0") return false;
  return defaultWhenMissing;
}

function formatRupee(n: unknown): string {
  if (n == null || n === "") return "—";
  const num = typeof n === "number" ? n : Number.parseFloat(String(n));
  return Number.isFinite(num) ? `${num}` : String(n);
}

/** Status for display/filter: API status, or Expired when past expiry date. */
export function getEffectivePromoStatusCodeValue(
  row: PromoCodeRow,
  expiredStatusCodeValue?: string,
): string {
  if (row.isExpiredByDate && expiredStatusCodeValue) {
    return expiredStatusCodeValue;
  }
  return row.statusCodeValue;
}

function promoRowMatchesStatusFilter(
  row: PromoCodeRow,
  statusFilter: string,
  expiredStatusCodeValue?: string,
): boolean {
  if (statusFilter === "" || statusFilter == null) return true;
  return (
    String(getEffectivePromoStatusCodeValue(row, expiredStatusCodeValue)) ===
    String(statusFilter)
  );
}

/** Max rows loaded for client-side status filter + pagination. */
const PROMO_LIST_FETCH_PAGE_SIZE = 500;

// discounttype 2 (or percent) → % display; else rupee.
function buildDiscountValueCell(item: Record<string, unknown>): string {
  const preset = item.discountvaluedisplay ?? item.discountValueDisplay;
  if (preset != null && String(preset).trim() !== "") return String(preset);
  const typeNum = item.discounttype ?? item.discountType;
  const valRaw = item.discountvalue ?? item.discountValue;
  const num =
    typeof valRaw === "number"
      ? valRaw
      : Number.parseFloat(String(valRaw ?? ""));
  const isPercent =
    typeNum === 2 ||
    typeNum === "2" ||
    String(typeNum).toLowerCase() === "percent" ||
    String(typeNum).toLowerCase() === "percentage";
  if (isPercent) return Number.isFinite(num) ? `${num}%` : "—";
  return formatRupee(valRaw);
}

function buildValidityRange(item: Record<string, unknown>): string {
  const preset =
    item.validitydates ??
    item.validityDates ??
    item.validityrange ??
    item.validityRange;
  if (preset != null && String(preset).trim() !== "") return String(preset);
  const start =
    item.startdate ?? item.startDate ?? item.validfrom ?? item.validFrom;
  const end = pickPromoExpiryRaw(item);
  const a = start ? DATE_FORMATS.formatDate(String(start)) : "";
  const b = end ? DATE_FORMATS.formatDate(String(end)) : "";
  if (a && b) return `${a} - ${b}`;
  if (a) return a;
  if (b) return b;
  return "—";
}

// Normalizes searchPromoCodes list items (mixed casing / aliases) → table row.
export function mapPromoApiItemToRow(
  item: Record<string, unknown>,
  index: number,
  options?: { expiredStatusCodeValue?: string },
): PromoCodeRow {
  const id = String(
    item.couponid ??
      item.couponId ??
      item.promomasterid ??
      item.promoMasterId ??
      item.promocodeid ??
      item.promoCodeId ??
      item.id ??
      `promo-${index}`,
  );
  const codeName = String(
    item.couponname ??
      item.couponName ??
      item.codename ??
      item.codeName ??
      item.promoCode ??
      item.promocode ??
      "",
  );
  const isExpiredByDate = isPromoExpiredByExpiryDate(item);
  const statusCodeValue = resolvePromoStatusCodeValue(
    item,
    options?.expiredStatusCodeValue,
  );

  const usedRaw = item.usedcount ?? item.usedCount ?? 0;
  const usedCount =
    typeof usedRaw === "number"
      ? usedRaw
      : Number.parseInt(String(usedRaw), 10) || 0;

  const limitRaw = item.usagelimit ?? item.usageLimit ?? 0;
  const usageLimit =
    typeof limitRaw === "number"
      ? limitRaw
      : Number.parseInt(String(limitRaw), 10) || 0;

  const descRaw = item.description ?? item.Description ?? "";
  const description =
    descRaw != null && String(descRaw).trim() !== "" ? String(descRaw) : "—";

  const referralRaw =
    item.isreferralcoupon ?? item.isReferralCoupon ?? item.is_referral_coupon;
  const isReferralCoupon =
    referralRaw === true ||
    referralRaw === 1 ||
    String(referralRaw).toLowerCase() === "true";

  const row: PromoCodeRow = {
    id,
    codeName,
    description,
    discountType: Number(item.discounttype ?? item.discountType ?? 0),
    type: Number(item.discounttype ?? item.discountType ?? 0),
    discountValue: buildDiscountValueCell(item),
    minBooking: formatRupee(
      item.minbookingamount ??
        item.minBookingAmount ??
        item.minbooking ??
        item.minBooking ??
        item.minimumbookingamount ??
        item.minimumBookingAmount,
    ),
    validityDates: buildValidityRange(item),
    isExpiredByDate,
    statusCodeValue,
    usedCount,
    usageLimit,
    isReferralCoupon,
    isUserAssignable: parsePromoListBool(
      item.is_user_assignable ?? item.isUserAssignable,
      false,
    ),
    isCouponReuseable: parsePromoListBool(
      item.is_coupon_reuseable ?? item.isCouponReuseable,
      false,
    ),
  };
  return row;
}

const StatusChip: React.FC<{ label: string; codeValue: string }> = ({
  label,
  codeValue,
}) => {
  const lower = `${label} ${codeValue}`.toLowerCase();
  // Heuristic when API has many status codes (not only Active/Expired).
  const isPositive =
    !/(expir|inactive|in-active|void|cancel|closed|disabled|delet)/i.test(
      lower,
    );
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        fontWeight: 600,
        backgroundColor: isPositive
          ? "rgba(212, 255, 199, 1)"
          : "rgba(255, 227, 227, 1)",
        color: isPositive ? "rgba(31, 113, 13, 1)" : "rgba(197, 0, 0, 1)",
      }}
    />
  );
};

const PromoCodesPage: React.FC = () => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { showNotification } = useNotification();
  // CommonService: CouponDiscountType / CouponType / PromoCodeStatus (displayorder in hook).
  const { statusCodes: couponDiscountTypeCodes } =
    useCodeCommonByCategory("CouponDiscountType");
  const { statusCodes: couponTypeCodes, loading: couponTypeLoading } =
    useCodeCommonByCategory("CouponType");
  const { statusCodes: promoStatusCodes, loading: promoStatusLoading } =
    useCodeCommonByCategory("PromoCodeStatus");
  const [couponTypeFilter, setCouponTypeFilter] = useState(
    () =>
      (routerLocation.state as PromoListLocationState | null)?.couponType ??
      (routerLocation.state as PromoListLocationState | null)
        ?.couponDiscountType ??
      "1",
  );
  const [codeNameFilter, setCodeNameFilter] = useState(
    () =>
      (routerLocation.state as PromoListLocationState | null)?.codeName ?? "",
  );
  const [statusFilter, setStatusFilter] = useState(
    () => (routerLocation.state as PromoListLocationState | null)?.status ?? "",
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allRows, setAllRows] = useState<PromoCodeRow[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [promoPendingDelete, setPromoPendingDelete] = useState<{
    id: string;
    codeName: string;
  } | null>(null);

  const expiredStatusCodeValue = useMemo(
    () => findExpiredPromoStatusCodeValue(promoStatusCodes),
    [promoStatusCodes],
  );

  const filteredRows = useMemo(
    () =>
      allRows.filter((row) =>
        promoRowMatchesStatusFilter(row, statusFilter, expiredStatusCodeValue),
      ),
    [allRows, statusFilter, expiredStatusCodeValue],
  );

  const pagination = usePagination({
    totalItems: filteredRows.length,
    initialPage:
      (routerLocation.state as PromoListLocationState | null)?.page ?? 0,
    initialRowsPerPage: 10,
  });

  const pageRows = useMemo(() => {
    const start = pagination.page * pagination.rowsPerPage;
    return filteredRows.slice(start, start + pagination.rowsPerPage);
  }, [filteredRows, pagination.page, pagination.rowsPerPage]);

  const fetchPromoCodes = useCallback(async () => {
    setLoading(true);
    setError(null);
    const couponTypePayload =
      couponTypeFilter.trim().length > 0 ? couponTypeFilter.trim() : null;
    try {
      const response = await apiService.searchPromoCodes({
        codeName: codeNameFilter.trim(),
        status: null,
        coupontype: couponTypePayload,
        pageNumber: 1,
        pageSize: PROMO_LIST_FETCH_PAGE_SIZE,
      });
      const { items } = parsePromoCodesListResponse(response);
      const list = Array.isArray(items) ? items : [];
      setAllRows(
        list.map((raw, i) =>
          mapPromoApiItemToRow(raw as Record<string, unknown>, i, {
            expiredStatusCodeValue,
          }),
        ),
      );
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to fetch promo codes";
      setError(message);
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  }, [codeNameFilter, couponTypeFilter, expiredStatusCodeValue]);

  useEffect(() => {
    void fetchPromoCodes();
  }, [fetchPromoCodes]);

  const openDeletePromo = useCallback((row: PromoCodeRow) => {
    setPromoPendingDelete({ id: row.id, codeName: row.codeName });
    setDeleteDialogOpen(true);
  }, []);

  const cancelDeletePromo = useCallback(() => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setPromoPendingDelete(null);
  }, [deleteLoading]);

  const confirmDeletePromo = useCallback(async () => {
    if (!promoPendingDelete?.id) return;
    setDeleteLoading(true);
    setError(null);
    try {
      // API: deletePromoCode(couponid)
      await apiService.deletePromoCode(promoPendingDelete.id);
      showNotification("Promo code deleted successfully.");
      setDeleteDialogOpen(false);
      setPromoPendingDelete(null);
      await fetchPromoCodes();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to delete promo code";
      setError(message);
    } finally {
      setDeleteLoading(false);
    }
  }, [promoPendingDelete, fetchPromoCodes, showNotification]);

  const columns: SortableColumn<PromoCodeRow>[] = useMemo(
    () => [
      { id: "codeName", label: "Code Name" },
      {
        id: "description",
        label: "Description",
        render: (row) => (
          <Box
            title={row.description === "—" ? undefined : row.description}
            sx={{
              maxWidth: 280,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.description}
          </Box>
        ),
      },
      {
        id: "discountType",
        label: "Type",
        render: (row) => {
          const match = couponDiscountTypeCodes.find(
            (c) => String(c.codevalue) === String(row.discountType),
          );
          return (
            match?.codedisplayname ??
            (row.discountType === 0 ? "—" : String(row.discountType))
          );
        },
      },
      { id: "discountValue", label: "Discount Value" },
      { id: "minBooking", label: "Min Booking" },
      {
        id: "isUserAssignable",
        label: "User QR Code Assignable",
        headerRender: () => (
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="body2" className="fw-bold">
              User QR Code Assignable
            </Typography>
            <Tooltip title="Only available to users who scan a QR" arrow>
              <IconButton
                size="small"
                sx={{ p: 0, color: "text.secondary" }}
                aria-label="User QR Code Assignable info"
              >
                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
        align: "center",
        sortable: false,
        render: (row) => (
          <Typography
            variant="body2"
            sx={{ fontSize: "0.8125rem", whiteSpace: "nowrap" }}
          >
            {row.isUserAssignable ? "Yes" : "No"}
          </Typography>
        ),
      },
      {
        id: "isCouponReuseable",
        label: "Coupon Reuseable",
        align: "center",
        sortable: false,
        render: (row) => (
          <Typography variant="body2">
            {row.isCouponReuseable ? "Yes" : "No"}
          </Typography>
        ),
      },
      { id: "validityDates", label: "Validity Dates" },
      {
        id: "statusCodeValue",
        label: "Status",
        align: "center",
        render: (row) => {
          const codeValue = getEffectivePromoStatusCodeValue(
            row,
            expiredStatusCodeValue,
          );
          const match = promoStatusCodes.find(
            (c) => String(c.codevalue) === String(codeValue),
          );
          const label = match?.codedisplayname?.trim() || codeValue || "—";
          return <StatusChip label={label} codeValue={codeValue} />;
        },
      },
      {
        id: "usedCount",
        label: "Usage",
        align: "center",
        render: (row) => (
          <Box sx={{ whiteSpace: "nowrap" }}>
            {row.usedCount} / {row.usageLimit}
            {row.isReferralCoupon ? (
              <Chip
                label="Referral"
                size="small"
                sx={{ ml: 1, height: 22, fontSize: "0.7rem" }}
              />
            ) : null}
          </Box>
        ),
      },
      {
        id: "actions",
        label: "Action",
        align: "center",
        sortable: false,
        render: (row) => (
          <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
            <Button
              variant="text"
              size="small"
              sx={{ minWidth: 36, p: 0.5 }}
              onClick={() =>
                navigate(`/marketing/promo-codes/create?id=${row.id}`)
              }
              aria-label="Edit"
            >
              <img src={EditIcon} alt="" width={28} height={28} />
            </Button>
            <Button
              variant="text"
              size="small"
              sx={{ minWidth: 36, p: 0.5 }}
              onClick={() => openDeletePromo(row)}
              aria-label="Delete"
            >
              <img src={DeleteIcon} alt="" width={28} height={28} />
            </Button>
          </Box>
        ),
      },
    ],
    [
      navigate,
      openDeletePromo,
      couponDiscountTypeCodes,
      promoStatusCodes,
      expiredStatusCodeValue,
    ],
  );

  return (
    <>
      <Container fluid className="px-0">
        {error && (
          <Alert
            severity="error"
            className="mb-3"
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        <Paper className="mb-3 p-3 border-0 rounded-0 shadow-none">
          <Row className="g-3 align-items-end">
            <Col xs={12} md={3}>
              <TextField
                label="Code Name"
                placeholder="Search by code name"
                size="small"
                fullWidth
                value={codeNameFilter}
                onChange={(e) => {
                  const value = e.target.value;
                  setCodeNameFilter(value);
                  pagination.goToPage(0);
                  navigate(routerLocation.pathname, {
                    replace: true,
                    state: {
                      ...((routerLocation.state as object) || {}),
                      codeName: value,
                      status: statusFilter,
                      couponType: couponTypeFilter,
                      page: 0,
                    },
                  });
                }}
              />
            </Col>
            <Col xs={12} md={3}>
              <SearchableSelectInput
                field="status"
                formData={{ status: statusFilter || "" }}
                onChange={(_field: string, value: string) => {
                  setStatusFilter(value);
                  pagination.goToPage(0);
                  navigate(routerLocation.pathname, {
                    replace: true,
                    state: {
                      ...((routerLocation.state as object) || {}),
                      codeName: codeNameFilter,
                      status: value,
                      couponType: couponTypeFilter,
                      page: 0,
                    },
                  });
                }}
                label="Status"
                placeholder="Select status"
                options={promoStatusCodes.map((c) => ({
                  label: c.codedisplayname,
                  value: c.codevalue,
                }))}
                disabled={promoStatusLoading}
                loading={promoStatusLoading}
              />
            </Col>
            <Col xs={12} md={3}>
              <SearchableSelectInput
                field="couponType"
                formData={{ couponType: couponTypeFilter || "" }}
                onChange={(_field: string, value: string) => {
                  const nextCouponType = value.trim() === "" ? "1" : value;
                  setCouponTypeFilter(nextCouponType);
                  pagination.goToPage(0);
                  navigate(routerLocation.pathname, {
                    replace: true,
                    state: {
                      ...((routerLocation.state as object) || {}),
                      codeName: codeNameFilter,
                      status: statusFilter,
                      couponType: nextCouponType,
                      page: 0,
                    },
                  });
                }}
                label="Coupon Type"
                placeholder="Select coupon type"
                options={couponTypeCodes.map((c) => ({
                  label: c.codedisplayname,
                  value: c.codevalue,
                }))}
                disabled={couponTypeLoading}
                loading={couponTypeLoading}
              />
            </Col>
          </Row>
        </Paper>

        <Paper className="border-0 rounded-0 shadow-none">
          {loading ? (
            <CustomLoading message="Fetching promo codes..." />
          ) : (
            <SortableTable
              data={pageRows}
              columns={columns}
              emptyMessage="No promo codes found"
            />
          )}
        </Paper>

        <Pagination
          page={pagination.page}
          rowsPerPage={pagination.rowsPerPage}
          totalItems={filteredRows.length}
          totalPages={pagination.totalPages}
          startItem={pagination.startItem}
          endItem={pagination.endItem}
          rowsPerPageOptions={[5, 10, 25, 50]}
          showRowsPerPage
          onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          onGoToNextPage={pagination.goToNextPage}
          onGoToPreviousPage={pagination.goToPreviousPage}
        />
      </Container>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        message={
          promoPendingDelete
            ? `Are you sure you want to delete promo code "${promoPendingDelete.codeName}"? This action cannot be undone.`
            : ""
        }
        loading={deleteLoading}
        onCancel={cancelDeletePromo}
        onConfirm={() => void confirmDeletePromo()}
      />
    </>
  );
};

export default PromoCodesPage;
